import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Settings as SettingsIcon, Save, Plus, X, Layout, Image as ImageIcon,
  Users, Link as LinkIcon, Grid3x3, Sparkles, Boxes, ListChecks, Award,
  Megaphone, Mail, Presentation, ArrowRight, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import MediaPicker from "@/admin/components/MediaPicker";
import {
  SolutionsOverviewTab, WhyAnteraTab, ProductsShowcaseTab, FeaturesGridTab,
  SuccessStoryTab, CTAsTab, ContactPageTab, PresentationFeatureTab,
} from "@/admin/HomepageSectionTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  homepage: {
    badge_text: "Cloud ERP · CRM · eCommerce",
    headline_lead: "Leading",
    headline_highlight: "Promotional Products",
    headline_trail: "Software",
    intro: "From promotional products to print, our software helps your business grow by simplifying operations, reducing errors, and fueling growth and innovation.",
    hero_image: "/api/media/acd5f68d-154c-447a-9ae5-087a270de539",
    tagline: "18 Years serving the Promo, Print and Signage Industries",
    cta_primary_label: "Request a Demo",
    cta_primary_href: "/contact",
    cta_secondary_label: "Explore Solutions",
    cta_secondary_href: "/solutions",
  },
  suppliers: [],
  testimonials: [],
  social_links: { youtube: "", instagram: "", facebook: "", linkedin: "", x: "" },
  contact_info: { email: "sales@anterasoftware.com", phone: "+1-214-556-8040", address: "5301 Headquarters Dr, Ste #350, Plano, TX 75024" },
};

const TABS = [
  { id: "homepage", label: "Home Hero", icon: Layout },
  { id: "presentation_feature", label: "Presentation", icon: Presentation },
  { id: "solutions_overview", label: "Solutions", icon: Grid3x3 },
  { id: "why_antera", label: "Why Antera", icon: Sparkles },
  { id: "products_showcase", label: "Products", icon: Boxes },
  { id: "features_grid", label: "Features", icon: ListChecks },
  { id: "success_story", label: "Success Story", icon: Award },
  { id: "ctas", label: "CTAs", icon: Megaphone },
  { id: "contact_page", label: "Contact Page", icon: Mail },
  { id: "suppliers", label: "Suppliers", icon: ImageIcon },
  { id: "testimonials", label: "Testimonials", icon: Users },
  { id: "social", label: "Social & Contact", icon: LinkIcon },
];

export default function AdminSiteSettingsPage() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("homepage");
  const [mediaTarget, setMediaTarget] = useState(null); // { key: "homepage.hero_image" } | { type: "supplier-logo", index: 0 }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-settings`);
        setSettings({
          ...DEFAULTS,
          ...data,
          homepage: { ...DEFAULTS.homepage, ...(data.homepage || {}) },
          social_links: { ...DEFAULTS.social_links, ...(data.social_links || {}) },
          contact_info: { ...DEFAULTS.contact_info, ...(data.contact_info || {}) },
          suppliers: data.suppliers || [],
          testimonials: data.testimonials || [],
        });
      } catch {
        toast.error("Failed to load site settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (path, value) => {
    setSettings((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/site-settings`, settings);
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleMediaPicked = (url) => {
    if (!mediaTarget) return;
    if (mediaTarget.key) {
      update(mediaTarget.key, url);
    } else if (mediaTarget.type === "supplier-logo") {
      const next = [...settings.suppliers];
      next[mediaTarget.index] = { ...next[mediaTarget.index], logo_url: url };
      setSettings((prev) => ({ ...prev, suppliers: next }));
    }
    setMediaTarget(null);
  };

  // --- list helpers ---
  const addSupplier = () =>
    setSettings((prev) => ({
      ...prev,
      suppliers: [...prev.suppliers, { name: "", url: "", logo_url: "" }],
    }));
  const updateSupplier = (index, patch) =>
    setSettings((prev) => ({
      ...prev,
      suppliers: prev.suppliers.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));
  const removeSupplier = (index) =>
    setSettings((prev) => ({ ...prev, suppliers: prev.suppliers.filter((_, i) => i !== index) }));

  const addTestimonial = () =>
    setSettings((prev) => ({
      ...prev,
      testimonials: [...prev.testimonials, { quote: "", author: "", company: "" }],
    }));
  const updateTestimonial = (index, patch) =>
    setSettings((prev) => ({
      ...prev,
      testimonials: prev.testimonials.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }));
  const removeTestimonial = (index) =>
    setSettings((prev) => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index),
    }));

  if (loading) {
    return (
      <div className="p-10">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-site-page" className="bg-slate-50 min-h-full">
      <Toaster position="top-right" richColors />

      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-5 lg:px-8 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-900">
          <SettingsIcon className="h-4 w-4 text-sky-600" />
          <h1 className="text-base font-bold">Site Settings</h1>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="admin-site-save"
          className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
        >
          <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="px-5 lg:px-8 pt-4">
        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`admin-site-tab-${t.id}`}
              className={`px-4 py-2.5 text-xs font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-sky-600 text-sky-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5 lg:p-8 max-w-5xl mx-auto">
        {tab === "homepage" && (
          <section className="bg-white border border-slate-200 rounded-xl p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center shrink-0">
                <Info className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">The Homepage moved to the Page Editor</h2>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed max-w-2xl">
                  Homepage content — hero badge, headlines, intro, tagline, CTAs, hero image, all sections and every block on the homepage — is now edited in the block-based Page Editor. The old fields on this screen no longer drive the live site.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/site-pages/home">
                <Button
                  data-testid="site-settings-open-home-editor"
                  className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg h-10 px-5 text-sm font-semibold inline-flex items-center gap-2"
                >
                  Open the Homepage Editor <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/admin/site-pages">
                <Button
                  variant="outline"
                  data-testid="site-settings-open-all-pages"
                  className="h-10 px-5 text-sm font-semibold rounded-lg"
                >
                  See all editable pages
                </Button>
              </Link>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100">
              <p className="text-xs text-slate-500 leading-relaxed">
                <strong className="text-slate-700">Where to find things now:</strong> Hero, Why Antera cards, Success Story, Products Showcase, Features Grid, Solutions Overview, CTAs, Testimonials, Partner Logos, and Contact Form are all blocks inside the Homepage Editor. Add, remove, reorder, and edit them there.
                <br /><br />
                <strong className="text-slate-700">This screen (Site Settings) is still used for:</strong> Site-wide navigation, footer content, social links, contact info (email/phone/address), and SEO defaults. Those tabs continue to work as before.
              </p>
            </div>
          </section>
        )}

        {tab === "presentation_feature" && (
          <PresentationFeatureTab
            settings={settings}
            update={update}
            openMediaPicker={(key) => setMediaTarget({ key })}
          />
        )}

        {tab === "solutions_overview" && (
          <SolutionsOverviewTab settings={settings} update={update} />
        )}

        {tab === "why_antera" && (
          <WhyAnteraTab settings={settings} update={update} />
        )}

        {tab === "products_showcase" && (
          <ProductsShowcaseTab
            settings={settings}
            update={update}
            openMediaPicker={(key) => setMediaTarget({ key })}
          />
        )}

        {tab === "features_grid" && (
          <FeaturesGridTab settings={settings} update={update} />
        )}

        {tab === "success_story" && (
          <SuccessStoryTab
            settings={settings}
            update={update}
            openMediaPicker={(key) => setMediaTarget({ key })}
          />
        )}

        {tab === "ctas" && (
          <CTAsTab settings={settings} update={update} />
        )}

        {tab === "contact_page" && (
          <ContactPageTab settings={settings} update={update} />
        )}

        {tab === "suppliers" && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-900">Elite Suppliers</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Logos shown in the homepage marquee. Order is preserved. {settings.suppliers.length} supplier(s).
                </p>
              </div>
              <Button
                type="button"
                onClick={addSupplier}
                data-testid="admin-site-supplier-add"
                className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add supplier
              </Button>
            </div>

            {settings.suppliers.length === 0 ? (
              <p className="text-xs text-slate-400 italic">
                No suppliers yet - these will fall back to the built-in list until you add some.
              </p>
            ) : (
              <div className="space-y-3">
                {settings.suppliers.map((s, i) => (
                  <div
                    key={i}
                    data-testid={`admin-site-supplier-row-${i}`}
                    className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 flex items-center gap-3"
                  >
                    <div className="w-16 h-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                      {s.logo_url ? (
                        <img src={s.logo_url} alt={s.name} className="max-w-full max-h-full object-contain" />
                      ) : (
                        <ImageIcon className="h-5 w-5 text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input
                        value={s.name}
                        onChange={(e) => updateSupplier(i, { name: e.target.value })}
                        placeholder="SanMar"
                        className="h-9 text-xs"
                      />
                      <Input
                        value={s.url || ""}
                        onChange={(e) => updateSupplier(i, { url: e.target.value })}
                        placeholder="https://sanmar.com (optional)"
                        className="h-9 text-xs font-mono"
                      />
                      <Input
                        value={s.logo_url || ""}
                        onChange={(e) => updateSupplier(i, { logo_url: e.target.value })}
                        placeholder="Logo URL"
                        className="h-9 text-xs font-mono md:col-span-2"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setMediaTarget({ type: "supplier-logo", index: i })}
                      className="h-9 text-xs rounded-lg shrink-0"
                    >
                      Pick logo
                    </Button>
                    <button
                      type="button"
                      onClick={() => removeSupplier(i)}
                      className="text-slate-400 hover:text-red-500 p-1 shrink-0"
                      aria-label="Remove supplier"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "testimonials" && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-900">Customer Testimonials</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Shown in the &ldquo;What our customers are saying&rdquo; section. {settings.testimonials.length} testimonial(s).
                </p>
              </div>
              <Button
                type="button"
                onClick={addTestimonial}
                data-testid="admin-site-testimonial-add"
                className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add testimonial
              </Button>
            </div>

            {settings.testimonials.length === 0 ? (
              <p className="text-xs text-slate-400 italic">
                No testimonials yet - the site will use the built-in defaults.
              </p>
            ) : (
              <div className="space-y-3">
                {settings.testimonials.map((t, i) => (
                  <div
                    key={i}
                    data-testid={`admin-site-testimonial-row-${i}`}
                    className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 space-y-2"
                  >
                    <Textarea
                      rows={2}
                      value={t.quote}
                      onChange={(e) => updateTestimonial(i, { quote: e.target.value })}
                      placeholder={'\u201cAntera has helped us\u2026\u201d'}
                      className="text-sm"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input
                        value={t.author}
                        onChange={(e) => updateTestimonial(i, { author: e.target.value })}
                        placeholder="Operations Lead"
                        className="h-9 text-xs"
                      />
                      <Input
                        value={t.company}
                        onChange={(e) => updateTestimonial(i, { company: e.target.value })}
                        placeholder="Promotional Products Distributor"
                        className="h-9 text-xs"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeTestimonial(i)}
                        className="text-[10px] text-slate-400 hover:text-red-500 inline-flex items-center gap-1"
                      >
                        <X className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "social" && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <h2 className="text-sm font-bold text-slate-900">Social & Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="YouTube">
                <Input
                  value={settings.social_links.youtube || ""}
                  onChange={(e) => update("social_links.youtube", e.target.value)}
                  placeholder="https://youtube.com/@anterausa"
                  className="font-mono text-xs"
                />
              </Field>
              <Field label="Instagram">
                <Input
                  value={settings.social_links.instagram || ""}
                  onChange={(e) => update("social_links.instagram", e.target.value)}
                  placeholder="https://instagram.com/anterausa/"
                  className="font-mono text-xs"
                />
              </Field>
              <Field label="Facebook">
                <Input
                  value={settings.social_links.facebook || ""}
                  onChange={(e) => update("social_links.facebook", e.target.value)}
                  placeholder="https://facebook.com/Anterasoftware"
                  className="font-mono text-xs"
                />
              </Field>
              <Field label="LinkedIn">
                <Input
                  value={settings.social_links.linkedin || ""}
                  onChange={(e) => update("social_links.linkedin", e.target.value)}
                  placeholder="https://linkedin.com/company/antera-software"
                  className="font-mono text-xs"
                />
              </Field>
              <Field label="X (Twitter)">
                <Input
                  value={settings.social_links.x || ""}
                  onChange={(e) => update("social_links.x", e.target.value)}
                  placeholder="https://x.com/AnteraUSA"
                  className="font-mono text-xs"
                />
              </Field>
            </div>

            <h3 className="text-sm font-bold text-slate-900 pt-4 border-t border-slate-100">Contact info</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Email">
                <Input
                  value={settings.contact_info.email || ""}
                  onChange={(e) => update("contact_info.email", e.target.value)}
                />
              </Field>
              <Field label="Phone">
                <Input
                  value={settings.contact_info.phone || ""}
                  onChange={(e) => update("contact_info.phone", e.target.value)}
                />
              </Field>
              <Field label="Address">
                <Textarea
                  rows={2}
                  value={settings.contact_info.address || ""}
                  onChange={(e) => update("contact_info.address", e.target.value)}
                />
              </Field>
            </div>
          </section>
        )}
      </div>

      <MediaPicker
        open={!!mediaTarget}
        onClose={() => setMediaTarget(null)}
        onPick={handleMediaPicked}
        mode="picker"
      />
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}
